import type { ApprovalProposalListItem } from "@adpilot/shared";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApprovalProposalRows } from "@/components/approval/approval-proposal-rows";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { RiskLegendTip } from "@/components/shared/RiskLegendTip";
import { Checkbox } from "@/components/ui/checkbox";
import { TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface ApprovalProposalTableProps {
  proposals: ApprovalProposalListItem[];
  now: Date;
  onReview: (proposalId: string) => void;
  footer?: ReactNode;
  selectedProposalIds?: ReadonlySet<string>;
  onSelectionChange?: (proposalId: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
}

export function ApprovalProposalTable({
  proposals,
  now,
  onReview,
  footer,
  selectedProposalIds,
  onSelectionChange,
  onSelectAll,
}: ApprovalProposalTableProps) {
  const { t } = useTranslation("approval");

  return (
    <DataTableFrame footer={footer}>
      <DataTable density="standard">
        <TableHeader>
          <TableRow className="bg-muted/20 hover:bg-muted/20">
            {selectedProposalIds && onSelectAll ? (
              <TableHead className="w-10">
                <Checkbox
                  aria-label={t("table.selectAll")}
                  checked={
                    proposals.length > 0 &&
                    proposals.every((proposal) => selectedProposalIds.has(proposal.id))
                  }
                  onCheckedChange={(checked) => onSelectAll(checked === true)}
                />
              </TableHead>
            ) : null}
            <TableHead>{t("table.headers.proposal")}</TableHead>
            <TableHead>{t("table.headers.group")}</TableHead>
            <TableHead>{t("table.headers.bidChange")}</TableHead>
            <TableHead>
              <span className="flex items-center gap-1">
                {t("table.headers.risk")}
                <RiskLegendTip aggregate />
              </span>
            </TableHead>
            <TableHead>{t("table.headers.created")}</TableHead>
            <TableHead className="text-right">{t("table.headers.deadline")}</TableHead>
            <TableHead className="w-20">
              <span className="sr-only">{t("table.review")}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {proposals.map((proposal) => (
            <ApprovalProposalRows
              key={proposal.runId}
              proposal={proposal}
              now={now}
              onReview={() => onReview(proposal.id)}
              selected={selectedProposalIds?.has(proposal.id)}
              onSelectedChange={
                onSelectionChange
                  ? (selected) => onSelectionChange(proposal.id, selected)
                  : undefined
              }
            />
          ))}
        </TableBody>
      </DataTable>
    </DataTableFrame>
  );
}
